import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, User, Building2 } from "lucide-react";

export default function Login() {
  const { login, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const redirect = sp.get("redirect");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [expectedRole, setExpectedRole] = useState("customer");
  const [loading, setLoading] = useState(false);
  const [lockSecs, setLockSecs] = useState(0);

  useEffect(() => {
    if (sessionStorage.getItem("mp_session_expired")) {
      sessionStorage.removeItem("mp_session_expired");
      toast.error("Session expired, please login again.");
    }
  }, []);

  useEffect(() => {
    if (lockSecs <= 0) return;
    const id = setInterval(() => setLockSecs((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [lockSecs]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, "public");
      if (u.role !== expectedRole && !(expectedRole === "broker" && u.role === "owner")) {
        logout();
        toast.error(`This account is registered as a ${u.role}. Please choose "${u.role}" above.`);
        return;
      }
      toast.success("Welcome back!");
      if (u.role === "broker" || u.role === "owner") navigate(redirect || "/broker");
      else navigate(redirect || "/browse");
    } catch (err) {
      const secs = parseInt(err.response?.headers?.["retry-after"] || "0", 10);
      if (err.response?.status === 429 && secs > 0) setLockSecs(secs);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-brand-ink text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #1f7a5c 0, transparent 40%), radial-gradient(circle at 80% 70%, #d9744f 0, transparent 45%)" }} />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-brand-emerald flex items-center justify-center"><Home className="w-5 h-5 text-white" /></div>
          <span className="font-heading font-bold text-xl">My Property</span>
        </div>
        <div className="relative z-10">
          <h2 className="font-heading font-bold text-4xl leading-tight">Find your perfect place,<br />wherever you like.</h2>
          <p className="text-white/70 mt-4 max-w-sm">Verified mediators, direct chat, and the best deals — all in one trusted platform.</p>
        </div>
        <div className="relative z-10 text-white/50 text-sm">mypropertyas.com</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <h1 className="font-heading font-bold text-3xl text-brand-ink">{t("auth.loginTitle")}</h1>
          <p className="text-brand-muted text-sm mt-1 mb-7">{t("tagline")}</p>

          {redirect && (
            <div className="mb-6 rounded-xl bg-brand-emerald/5 border border-brand-emerald/20 text-brand-ink text-sm px-4 py-3" data-testid="login-redirect-note">
              Please log in or create an account to view this property.
            </div>
          )}

          {/* Role selector — top */}
          <p className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-2">{t("role.loginAs")}</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {[{ v: "customer", icon: User, l: t("auth.customer") }, { v: "broker", icon: Building2, l: t("auth.broker") }].map((r) => (
              <button
                key={r.v}
                type="button"
                onClick={() => setExpectedRole(r.v)}
                data-testid={`login-role-${r.v}`}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all ${expectedRole === r.v ? "border-brand-emerald bg-brand-emerald/5 text-brand-emerald" : "border-brand-line text-brand-muted hover:border-brand-emerald/40"}`}
              >
                <r.icon className="w-4 h-4" /> <span className="text-sm font-semibold">{r.l}</span>
              </button>
            ))}
          </div>

          <div className="my-2" />

          <form onSubmit={submit} className="space-y-4">
            {lockSecs > 0 && (
              <div className="rounded-xl bg-brand-terracotta/5 border border-brand-terracotta/20 text-brand-terracotta text-sm px-4 py-3" data-testid="login-lockout-banner">
                Account locked after too many attempts. Try again in <span className="font-semibold tabular-nums">{Math.floor(lockSecs / 60)}:{String(lockSecs % 60).padStart(2, "0")}</span>
              </div>
            )}
            <div>
              <Label>{t("auth.email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1.5" />
            </div>
            <div>
              <Label>{t("auth.password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1.5" />
              <div className="text-right mt-1.5">
                <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-xs text-brand-emerald hover:underline">Forgot password?</Link>
              </div>
            </div>
            <Button type="submit" disabled={loading || lockSecs > 0} data-testid="login-submit" className="w-full rounded-full bg-brand-emerald hover:bg-brand-emeraldHover h-11">
              {lockSecs > 0 ? `Locked (${Math.floor(lockSecs / 60)}:${String(lockSecs % 60).padStart(2, "0")})` : loading ? t("loading") : t("auth.loginBtn")}
            </Button>
          </form>

          {/* Register — bottom */}
          <div className="mt-7 pt-5 border-t border-brand-line text-center">
            <p className="text-sm text-brand-muted">{t("auth.noAccount")}</p>
            <Link to={redirect ? `/register?redirect=${encodeURIComponent(redirect)}` : "/register"} data-testid="go-register" className="inline-block mt-2 text-brand-emerald font-semibold hover:underline">{t("nav.register")} →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
